import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6kpm1lv117fnkj2gfmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="relative bg-gradient-to-br from-emerald-800 to-emerald-900">
      {/* Upper section with contact info */}
      <div className="px-8 lg:px-24 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-semibold text-white mb-4">SOMA d.o.o.</h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              Expert team developing modern applications for the iOS platform, enhancing user experience and mobile security.
            </p>
          </div>

          {/* Get in Touch */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-6">Get in Touch</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <i className="ri-mail-line text-white text-xl"></i>
                <a href="mailto:contact@somadoo.com" className="text-base text-white hover:text-emerald-300 transition-colors cursor-pointer">
                  contact@somadoo.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-white text-xl"></i>
                <a href="tel:+16073885413" className="text-base text-white hover:text-emerald-300 transition-colors cursor-pointer">
                  +1 6073885413
                </a>
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-6">Location</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-white text-xl mt-1"></i>
                <div className="text-base text-white">
                  <p>Koprska ulica 78</p>
                  <p>Ljubljana 1000</p>
                  <p>Slovenia</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-6">Send Message</h4>
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors text-sm"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors text-sm"
              />
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors text-sm"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your Message"
                required
                maxLength={500}
                rows={3}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors resize-none text-sm"
              ></textarea>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-white text-emerald-800 rounded-lg font-medium hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-sm text-green-300">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-sm text-red-300">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Lower section with large SOMA text */}
      <div className="relative px-8 lg:px-24 py-16 overflow-hidden">
        <div className="text-center">
          <h2
            className="text-9xl lg:text-[180px] font-extrabold tracking-tight"
            style={{
              color: '#E8DCC4',
              textShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            SOMA
          </h2>
        </div>
      </div>
    </section>
  );
}